import { blocked as blockedResult, failed as failedResult } from 'flare-chat-core/contracts/chat/operation-result';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function normalizeError(error, fallbackMessage) {
  if (error instanceof Error) {
    return error;
  }
  if (error && typeof error === 'object') return Object.assign(new Error(error.message || fallbackMessage), error);
  return new Error(String(error || fallbackMessage));
}

export function useAppActionErrorGuards({
  scopeKey = '',
  blocked = false,
  blockedError,
  sessionListError = null,
  composerError = null,
  onCreateProject,
  onCreateSession,
  onSend,
  onRespondInput,
  onRetry,
} = {}) {
  const [actionError, setActionError] = useState(null);
  const [sendError, setSendError] = useState(null);
  const scope = useMemo(() => ({ key: scopeKey, active: true }), [scopeKey]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  useEffect(() => { scope.active = true; return () => { scope.active = false; }; }, [scope]);
  const [createProjectPending, setCreateProjectPending] = useState(false);
  const createProjectPendingRef = useRef(false);

  const handleCreateProject = useCallback(async (options = {}) => {
    if (blocked) {
      setActionError(blockedError);
      return;
    }
    if (createProjectPendingRef.current) {
      return;
    }
    createProjectPendingRef.current = true;
    setCreateProjectPending(true);
    try {
      setActionError(null);
      await onCreateProject?.(options);
    } catch (nextError) {
      setActionError(normalizeError(nextError, ''));
    } finally {
      createProjectPendingRef.current = false;
      setCreateProjectPending(false);
    }
  }, [blocked, blockedError, onCreateProject]);

  const handleCreateSession = useCallback(async (options) => {
    if (blocked) {
      setActionError(blockedError);
      return;
    }
    try {
      setActionError(null);
      await onCreateSession?.(options);
    } catch (nextError) {
      setActionError(normalizeError(nextError, ''));
    }
  }, [blocked, blockedError, onCreateSession]);

  // Observe public outcomes only; command shaping and lifecycle admission live upstream.
  const observe = useCallback(async (operation, invoke) => {
    const owner = currentScope.current;
    const isCurrent = () => owner.active && currentScope.current === owner;
    const resultError = (result) => result?.error || (result?.status === 'blocked'
      ? Object.assign(new Error('当前暂时无法提交，请稍后重试。'), { code: result.reason, reason: result.reason }) : null);
    if (isCurrent()) setSendError({ owner, error: null });
    try {
      const result = blocked ? blockedResult('connection_unavailable') : await invoke();
      if (isCurrent()) setSendError({ owner, error: resultError(result) || null });
      if (blocked && isCurrent()) setSendError({ owner, error: blockedError || resultError(result) });
      return result;
    } catch (error) {
      if (isCurrent()) setSendError({ owner, error: normalizeError(error, '') });
      return failedResult(error, operation);
    }
  }, [blocked, blockedError]);
  const handleSend = useCallback((options = {}) => observe('send', () => onSend?.(options)
    ?? blockedResult('operation_unavailable')), [observe, onSend]);
  const handleRespondInput = useCallback((input) => observe('submit_input', () => onRespondInput?.(input)
    ?? blockedResult('operation_unavailable')), [observe, onRespondInput]);

  const handleRetry = useCallback(() => observe('retry', () => onRetry?.()
    ?? blockedResult('operation_unavailable')), [observe, onRetry]);

  return useMemo(() => ({
    actionError,
    createProjectPending,
    handleCreateProject,
    handleCreateSession,
    handleSend,
    handleRespondInput,
    handleRetry,
    sessionPaneError: actionError || sessionListError || null,
    streamError: (sendError?.owner === scope ? sendError.error : null) || composerError || null,
  }), [actionError, sendError, scope, composerError, createProjectPending, handleCreateProject, handleCreateSession, handleSend, handleRespondInput, handleRetry, sessionListError]);
}
